import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

/**
 * Initialize Sentry for error tracking
 *
 * Environment variables (set in .envs/.local/.react or .envs/.production/.react):
 * - REACT_APP_SENTRY_ENABLED: Set to 'true' to enable Sentry (default: 'false')
 * - REACT_APP_SENTRY_DSN: Sentry DSN for error reporting
 * - REACT_APP_SENTRY_ENVIRONMENT: Environment name (defaults to NODE_ENV)
 * - REACT_APP_VERSION: Release version for Sentry
 * - REACT_APP_SENTRY_TRACES_SAMPLE_RATE: 0.0..1.0 (default 1.0 dev, 0.1 prod)
 * - REACT_APP_SENTRY_PROFILES_SAMPLE_RATE: 0.0..1.0 (default 1.0 dev, 0.1 prod)
 * - REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: 0.0..1.0 (default 0.5 dev, 0.1 prod)
 * - REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE: 0.0..1.0 (default 1.0)
 * - REACT_APP_SENTRY_CAPTURE_CONSOLE_ERRORS: 'true' to promote console.error to Sentry events
 */
export const initSentry = () => {
  // Check if Sentry is enabled via environment variable
  const isEnabled = process.env.REACT_APP_SENTRY_ENABLED === 'true';

  if (!isEnabled) {
    console.log('Sentry is disabled. Set REACT_APP_SENTRY_ENABLED=true to enable.');
    return;
  }

  const dsn = process.env.REACT_APP_SENTRY_DSN;

  // If enabled but no DSN provided, warn and return
  if (!dsn) {
    console.warn('Sentry is enabled but DSN not provided. Set REACT_APP_SENTRY_DSN. Error tracking is disabled.');
    return;
  }

  const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const release = process.env.REACT_APP_VERSION || 'unknown';
  const dev = environment !== 'production';

  // Sample rates (configurable via env)
  const tracesSampleRate = parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? (dev ? '1.0' : '0.1'));
  const profilesSampleRate = parseFloat(process.env.REACT_APP_SENTRY_PROFILES_SAMPLE_RATE ?? (dev ? '1.0' : '0.1'));
  const replaysSessionSampleRate = parseFloat(process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? (dev ? '0.5' : '0.1'));
  const replaysOnErrorSampleRate = parseFloat(process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? '1.0');
  const sendDefaultPii = process.env.REACT_APP_SENTRY_SEND_DEFAULT_PII === 'true';
  const debug = process.env.REACT_APP_SENTRY_DEBUG === 'true';
  const replayMaskAllText = process.env.REACT_APP_SENTRY_REPLAYS_MASK_ALL_TEXT === 'true';
  const replayBlockAllMedia = process.env.REACT_APP_SENTRY_REPLAYS_BLOCK_ALL_MEDIA === 'true';

  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii,
    debug,
    // Send console logs to Sentry (info-level logging pipeline)
    enableLogs: true,

    // Performance monitoring
    integrations: [
      // React Router v6 instrumentation for accurate navigation spans
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.browserTracingIntegration({
        // Trace route navigations and XHR/fetch
        tracePropagationTargets: [/.*/],
      }),
      // Session Replays
      Sentry.replayIntegration({
        maskAllText: replayMaskAllText,
        blockAllMedia: replayBlockAllMedia,
      }),
      // JS Profiling
      Sentry.browserProfilingIntegration(),
      // Console logging (log, warn, error) to Sentry Logs
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],

    // Performance and profiling sample rates
    tracesSampleRate,
    profilesSampleRate,

    // Replays sample rates
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,

    // Capture unhandled promise rejections
    captureUnhandledRejections: true,

    // Capture uncaught exceptions
    beforeSend(event, hint) {
      // In development, log to console for debugging
      if (environment === 'development') {
        console.error('Sentry Event:', event);
        console.error('Sentry Hint:', hint);
      }
      return event;
    },

    // Set initial scope tags
    initialScope: {
      tags: {
        component: 'frontend',
      },
    },
  });

  console.log(`Sentry initialized for ${environment} environment`);

  // Optionally send console.error as Sentry error events
  if (process.env.REACT_APP_SENTRY_CAPTURE_CONSOLE_ERRORS === 'true') {
    try {
      const originalError = window.console.error;
      window.console.error = function (...args) {
        try {
          const msg = args.map(a => (a instanceof Error ? `${a.name}: ${a.message}` : typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ');
          Sentry.captureMessage(msg, { level: 'error' });
        } catch {}
        if (originalError) originalError.apply(window.console, args);
      };
    } catch {}
  }
};
